import { useState, useRef, useCallback, useId, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import infoCircleIcon from '../../images/infoCircle.svg';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import './hoverTooltip.css';

const HoverTooltip = ({ heading, body, children, className = '', tabIndex = 0 }) => {
  const wrapperRef = useRef(null);
  const boxRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  // Horizontal pixel offset (from the box's own center) the arrow needs to shift by to
  // keep pointing at the trigger icon after clampPosition (below) has shifted the whole
  // box left/right to stay on-screen. 0 when no clamping was needed.
  const [arrowOffset, setArrowOffset] = useState(0);
  // "You are now leaving..." confirmation modal for external links embedded
  // in the tooltip's `body` HTML (e.g. Accreditation/Board-certified bodies'
  // raw <a href="..." target="_blank"> strings, injected via
  // dangerouslySetInnerHTML below). Those are plain DOM anchors with no React
  // click handler of their own, so clicks on them are intercepted via event
  // delegation (handleBodyClick) rather than per-link React props.
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);
  // Stable id for the tooltip content, referenced via aria-describedby below. The
  // tooltip's DOM node is now always rendered (see the unconditional createPortal
  // call further down) rather than mounted only while tooltipPos is set — screen
  // readers resolve aria-describedby against whatever's in the DOM at the moment
  // the trigger receives focus, and a target that doesn't exist yet (as it didn't
  // under the old conditional-render approach, since React's state update/re-render
  // from onFocus lands a tick after the focus event itself) was frequently missed,
  // which is why testers reported the tooltip's message body never being read aloud
  // — only the aria-label ("More info about ... button") was announced.
  const tooltipId = useId();

  // Recomputes the tooltip's viewport position from the trigger icon's CURRENT
  // bounding rect (not a stale one captured at show-time) — used both to show the
  // tooltip initially and to reposition it live on scroll/resize while it's open (see
  // the effect below). Previously this only ever ran once, on mouseenter/focus — since
  // the tooltip is `position: fixed` (viewport-relative, by design, so it isn't clipped
  // by the ancestor `.results-list`'s `transform: translateZ(0)` the way an
  // absolutely-positioned tooltip would be), that one-time position became stale the
  // instant the page scrolled: the term scrolled out from under the tooltip while the
  // tooltip itself stayed pinned at its original screen coordinates — reported as "not
  // stuck to the terms, I am able to scroll away" / "cut off and stuck at the top of
  // screen, I can scroll and tool tip remains."
  const updatePosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top - 8, // Position above the icon
        left: rect.left + rect.width / 2, // Center horizontally
      });
    }
  }, []);

  const showTooltip = useCallback(() => {
    updatePosition();
  }, [updatePosition]);

  const hideTooltip = useCallback(() => {
    setTooltipPos(null);
    setArrowOffset(0);
  }, []);

  // Event delegation for the raw <a> tags injected via dangerouslySetInnerHTML
  // in the tooltip body — intercepts clicks anywhere inside the body, and if
  // the click landed on (or inside) an anchor, prevents the default direct
  // navigation and shows the "you are now leaving" confirmation modal instead.
  const handleBodyClick = useCallback((e) => {
    const anchor = e.target.closest('a[href]');
    if (anchor) {
      e.preventDefault();
      // Hide the tooltip itself as soon as the modal opens — it's still
      // visually hovered/open at this point (the click happened inside it),
      // so without this its own (higher, see hoverTooltip.css) z-index and
      // active pointer-events would sit on top of the modal and swallow
      // clicks meant for its "Go on"/cancel buttons.
      setTooltipPos(null);
      setPendingExternalUrl(anchor.href);
      setExternalLinkModalOpen(true);
    }
  }, []);

  const handleExternalLinkConfirm = useCallback(() => {
    if (pendingExternalUrl) {
      window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
    }
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  }, [pendingExternalUrl]);

  const handleExternalLinkCancel = useCallback(() => {
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  }, []);

  const isVisible = !!tooltipPos;

  // Keep the tooltip "stuck" to its trigger term while the page scrolls, instead of
  // staying pinned at the fixed screen coordinates captured when it first opened (see
  // updatePosition's doc comment above for why that happened). Also closes it on
  // Escape and on any tap/click outside the trigger or the tooltip box itself — this
  // was the "unclear how to close" complaint: hover-driven mouseleave never fires on a
  // touch device (there's no mouse), so on mobile the ONLY way to dismiss a
  // touch-opened tooltip was tapping the trigger again — not obvious, and not
  // discoverable at all once the tooltip covered/moved away from the trigger.
  // Scroll listener uses capture:true so it also catches scroll events from any
  // scrollable ancestor (e.g. `.results-list`), not just window-level scrolling.
  useEffect(() => {
    if (!isVisible) return undefined;

    const handleScrollOrResize = () => updatePosition();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        hideTooltip();
        wrapperRef.current?.focus();
      }
    };

    const handlePointerDownOutside = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (boxRef.current?.contains(e.target)) return;
      hideTooltip();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('keydown', handleKeyDown);
    // 'pointerdown' (not 'click') so this also reliably fires on touch devices, and
    // fires before any click-triggered navigation on whatever was tapped.
    document.addEventListener('pointerdown', handlePointerDownOutside);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [isVisible, updatePosition, hideTooltip]);

  // Clamp the tooltip box horizontally within the viewport (8px margin on the left,
  // 18px on the right — see the extra right-side clearance comment below) after
  // every reposition, instead of letting the box's default right-edge-anchored
  // placement (see the render's `transform` below) push it fully or partially off
  // whichever edge of the screen the trigger happens to be near — the "tool tips
  // go off the screen in mobile, some are not usable at all" complaint. Narrow
  // mobile viewports make this especially likely since the box's own min-width
  // (289px, see hoverTooltip.css) is a large fraction of a typical phone's
  // viewport width. Shifts the arrow by the same amount so it still visually
  // points at the trigger icon even though the box itself has been nudged
  // on-screen.
  //
  // By design (per US: arrow should sit a fixed 20px from one end of the box
  // rather than floating to the horizontal center), the box's default
  // horizontal placement anchors its RIGHT edge 28px right of the trigger's
  // center — 20px arrow inset (see .hover-tooltip-arrow's `right: 20px` in
  // hoverTooltip.css) + 8px half the arrow triangle's own width — so the arrow
  // points exactly at the trigger with no further adjustment in the common
  // (non-clamped) case.
  useLayoutEffect(() => {
    if (!isVisible || !boxRef.current) return;

    const boxRect = boxRef.current.getBoundingClientRect();
    const margin = 8;
    // Extra clearance on the right edge specifically — a feedback/survey overlay
    // widget intermittently docks itself along the right edge of the screen on
    // mobile, and an 8px margin wasn't enough to keep the tooltip box from
    // rendering underneath/behind it. Left edge has no such overlay, so it
    // keeps the plain 8px margin.
    const rightMargin = margin + 10;
    const viewportWidth = window.innerWidth;

    const unclampedRightEdge = tooltipPos.left + 28;
    const unclampedLeftEdge = unclampedRightEdge - boxRect.width;

    let shift = 0;
    if (unclampedLeftEdge < margin) {
      shift = margin - unclampedLeftEdge;
    } else if (unclampedRightEdge > viewportWidth - rightMargin) {
      shift = (viewportWidth - rightMargin) - unclampedRightEdge;
    }

    // Widen/narrow the arrow's 20px-from-right-edge inset by the same amount the
    // box itself was shifted, so it keeps pointing at the trigger icon.
    setArrowOffset(shift);
    if (Math.abs(shift) > 0.5) {
      boxRef.current.style.transform = `translate(calc(-100% + ${28 + shift}px), -100%)`;
    } else {
      boxRef.current.style.transform = 'translate(calc(-100% + 28px), -100%)';
    }
  }, [isVisible, tooltipPos]);

  return (
    <div
      className={`hover-tooltip-wrapper ${className}`}
      ref={wrapperRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      tabIndex={tabIndex}
      role="button"
      aria-label={`More info about ${heading}`}
      aria-describedby={tooltipId}
    >
      {children || ""}
      {createPortal(
        <div
          id={tooltipId}
          ref={boxRef}
          className="hover-tooltip-portal"
          role="tooltip"
          // Also handle enter/leave on the portal content itself (rendered
          // elsewhere in the DOM via the portal, not inside the trigger wrapper
          // above) — without this, moving the mouse off the icon toward a link
          // inside the tooltip body fires the wrapper's onMouseLeave first and
          // hides the tooltip (opacity:0, pointer-events:none) before the click
          // on that link can ever register.
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          style={{
            position: 'fixed',
            top: (tooltipPos?.top ?? 0) + 'px',
            left: (tooltipPos?.left ?? 0) + 'px',
            // Default: box's right edge sits 28px right of the trigger's center
            // (see the clamping useLayoutEffect above for why 28px), so the
            // fixed 20px-from-right-edge arrow (hoverTooltip.css) points at the
            // trigger with no further adjustment needed. Overridden imperatively
            // when clamped to stay on-screen.
            transform: 'translate(calc(-100% + 28px), -100%)',
            // Visually hide when inactive WITHOUT display:none/visibility:hidden —
            // either of those removes the node from the accessibility tree, which
            // would defeat the whole point of always rendering it for
            // aria-describedby to reliably find.
            opacity: isVisible ? 1 : 0,
            pointerEvents: isVisible ? 'auto' : 'none',
          }}
          aria-hidden={!isVisible}
        >
          <div className="hover-tooltip-box">
            <div className="hover-tooltip-header">{heading}</div>
            {typeof body === 'string' && /<a\b[^>]*>/i.test(body) ? (
              <div
                className="hover-tooltip-body"
                onClick={handleBodyClick}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            ) : (
              <div className="hover-tooltip-body">{body}</div>
            )}
          </div>
          {/* Fixed 20px from the box's right edge by default (see hoverTooltip.css);
              widened/narrowed by `arrowOffset` when the box itself has been
              clamped on-screen (see the clamping effect above) so it keeps
              visually pointing at the trigger icon. */}
          <div
            className="hover-tooltip-arrow"
            style={arrowOffset ? { right: `calc(20px + ${arrowOffset}px)` } : undefined}
          ></div>
        </div>,
        document.body
      )}

      {/* Rendered via portal to document.body (see externalLinkModal's own
          position:fixed overlay, which — like the tooltip portal above —
          needs to escape any transformed ancestors to render as a proper
          centered fullscreen overlay instead of clipped/mispositioned). */}
      {externalLinkModalOpen && createPortal(
        <ExternalLinkModal
          isOpen={externalLinkModalOpen}
          onConfirm={handleExternalLinkConfirm}
          onCancel={handleExternalLinkCancel}
          url={pendingExternalUrl}
        />,
        document.body
      )}
    </div>
  );
};

export default HoverTooltip;
